/**
 * Bot Configuration Component
 *
 * Provides a comprehensive configuration form for Discord bots, including:
 * - Basic settings (name, description, tokens)
 * - LiteLLM endpoint dropdowns for LLM, TTS, STT, and Image services
 * - Voice settings and wake phrase configuration
 * - Auto-start and auto-restart toggles
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * Bot status enum matching backend
 */
type BotStatus = 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING' | 'ERROR';

/**
 * Endpoint type enum matching backend
 */
type EndpointType = 'LLM' | 'STT' | 'TTS' | 'IMAGE';

/**
 * LiteLLM endpoint dropdown item
 */
interface EndpointDropdownItem {
  id: string;
  name: string;
  modelName: string;
  backend: string;
  isGlobal: boolean;
  priority: number;
}

/**
 * Endpoints grouped by type for dropdowns
 */
interface EndpointsByType {
  LLM: EndpointDropdownItem[];
  STT: EndpointDropdownItem[];
  TTS: EndpointDropdownItem[];
  IMAGE: EndpointDropdownItem[];
}

/**
 * Search configuration for bot
 */
interface SearchConfigData {
  enabled: boolean;
  maxResults: number;
  includeInContext: boolean;
  toolCallEnabled: boolean;
  toolPrompt: string;
  safeSearchLevel: number;
}

/**
 * Free text mode configuration
 */
interface FreeTextModeConfig {
  enabled: boolean;
  systemPrompt?: string;
  delayMin?: number;
  delayMax?: number;
}

/**
 * Free voice mode configuration
 */
interface FreeVoiceModeConfig {
  enabled: boolean;
  systemPrompt?: string;
  delayMin?: number;
  delayMax?: number;
}

/**
 * Default search tool prompt
 */
const DEFAULT_SEARCH_TOOL_PROMPT = `You have access to a web search tool called "search". Use it when:
- The user asks about current events, news, or recent information
- You need up-to-date facts, statistics, or data
- The user asks about something you're not certain about
- The question requires real-time or frequently changing information

To use the search tool, call it with a search query. The results will be provided to help you answer the user's question accurately.`;

/**
 * Bot configuration structure
 */
interface BotConfig {
  llmEndpointId?: string;
  ttsEndpointId?: string;
  sttEndpointId?: string;
  imageEndpointId?: string;
  voiceProfileId?: string;
  systemPrompt?: string;
  memorySize?: number;
  wakePhrase?: string;
  voiceTriggerEnabled?: boolean;
  voiceIdleTimeout?: number;
  search?: SearchConfigData;
  freeTextMode?: FreeTextModeConfig;
  freeVoiceMode?: FreeVoiceModeConfig;
}

/**
 * Bot data structure
 */
interface Bot {
  id: string;
  name: string;
  description: string | null;
  status: BotStatus;
  autoStart: boolean;
  autoRestart: boolean;
  config: BotConfig;
  tenantId: string;
  lastStarted: string | null;
  lastStopped: string | null;
  createdAt: string;
  tenant?: {
    id: string;
    name: string;
    slug: string;
  };
  createdBy?: {
    id: string;
    name: string;
    email: string;
  };
}

/**
 * Form data for bot configuration
 */
interface BotFormData {
  name: string;
  description: string;
  discordToken: string;
  autoStart: boolean;
  autoRestart: boolean;
  config: BotConfig;
}

/**
 * Voice profile
 */
interface VoiceProfile {
  id: string;
  name: string;
  isGlobal: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<BotStatus, { bg: string; text: string; dot: string }> = {
  RUNNING: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  STOPPED: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
  STARTING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  STOPPING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  ERROR: { bg: 'bg-red-900/20', text: 'text-red-400', dot: 'bg-red-400' },
};

const STATUS_LABELS: Record<BotStatus, string> = {
  RUNNING: 'Running',
  STOPPED: 'Stopped',
  STARTING: 'Starting',
  STOPPING: 'Stopping',
  ERROR: 'Error',
};

const DEFAULT_FORM_DATA: BotFormData = {
  name: '',
  description: '',
  discordToken: '',
  autoStart: false,
  autoRestart: true,
  config: {
    memorySize: 20,
    voiceTriggerEnabled: true,
    voiceIdleTimeout: 300,
    wakePhrase: 'hey bot',
    systemPrompt: '',
    search: {
      enabled: true,
      maxResults: 5,
      includeInContext: true,
      toolCallEnabled: false,
      toolPrompt: DEFAULT_SEARCH_TOOL_PROMPT,
      safeSearchLevel: 1,
    },
  },
};

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
interface StatusBadgeProps {
  status: BotStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const colors = STATUS_COLORS[status];

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${colors.dot} ${
          status === 'STARTING' || status === 'STOPPING' ? 'animate-pulse' : ''
        }`}
      />
      {STATUS_LABELS[status]}
    </div>
  );
}

/**
 * Form section component
 */
interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
      <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-gray-400 mb-4">{description}</p>
      )}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

/**
 * Form input component
 */
interface FormInputProps {
  label: string;
  id: string;
  type?: 'text' | 'password' | 'number' | 'textarea';
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  min?: number;
  max?: number;
  rows?: number;
}

function FormInput({
  label,
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  disabled,
  helpText,
  error,
  min,
  max,
  rows = 3,
}: FormInputProps) {
  const inputClasses = `w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
    error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={inputClasses}
        />
      ) : (
        <input
          type={type}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          className={inputClasses}
        />
      )}
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Form toggle component
 */
interface FormToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function FormToggle({ label, description, checked, onChange, disabled }: FormToggleProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-gray-300">{label}</span>
        {description && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-discord-blurple' : 'bg-discord-light'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Endpoint select dropdown component
 */
interface EndpointSelectProps {
  label: string;
  type: EndpointType;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  endpoints: EndpointDropdownItem[];
  isLoading: boolean;
  disabled?: boolean;
  helpText?: string;
}

function EndpointSelect({
  label,
  type,
  value,
  onChange,
  endpoints,
  isLoading,
  disabled,
  helpText,
}: EndpointSelectProps) {
  const typeLabel = {
    LLM: 'Language Model',
    STT: 'Speech-to-Text',
    TTS: 'Text-to-Speech',
    IMAGE: 'Image Generation',
  }[type];

  // Group endpoints by global vs tenant-specific
  const groupedEndpoints = useMemo(() => {
    const global = endpoints.filter((e) => e.isGlobal);
    const tenant = endpoints.filter((e) => !e.isGlobal);
    return { global, tenant };
  }, [endpoints]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        <span className="text-gray-500 text-xs ml-2">({typeLabel})</span>
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={disabled || isLoading}
        className={`w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple ${
          disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <option value="">-- Select {typeLabel} --</option>
        {groupedEndpoints.tenant.length > 0 && (
          <optgroup label="Tenant Endpoints">
            {groupedEndpoints.tenant.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.name} ({endpoint.modelName} - {endpoint.backend})
              </option>
            ))}
          </optgroup>
        )}
        {groupedEndpoints.global.length > 0 && (
          <optgroup label="Global Endpoints">
            {groupedEndpoints.global.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.name} ({endpoint.modelName} - {endpoint.backend})
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
      {isLoading && (
        <p className="mt-1 text-xs text-gray-500">Loading endpoints...</p>
      )}
    </div>
  );
}

/**
 * Voice profile select dropdown component
 */
interface VoiceProfileSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  profiles: VoiceProfile[];
  isLoading: boolean;
  disabled?: boolean;
}

function VoiceProfileSelect({
  value,
  onChange,
  profiles,
  isLoading,
  disabled,
}: VoiceProfileSelectProps) {
  const groupedProfiles = useMemo(() => {
    const global = profiles.filter((p) => p.isGlobal);
    const tenant = profiles.filter((p) => !p.isGlobal);
    return { global, tenant };
  }, [profiles]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1.5">
        Voice Profile
        <span className="text-gray-500 text-xs ml-2">(for TTS)</span>
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={disabled || isLoading}
        className={`w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple ${
          disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <option value="">-- Default Voice --</option>
        {groupedProfiles.tenant.length > 0 && (
          <optgroup label="Tenant Voices">
            {groupedProfiles.tenant.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </optgroup>
        )}
        {groupedProfiles.global.length > 0 && (
          <optgroup label="Global Voices">
            {groupedProfiles.global.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {isLoading && (
        <p className="mt-1 text-xs text-gray-500">Loading voice profiles...</p>
      )}
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="flex items-center justify-between">
        <div className="h-8 w-64 bg-discord-light rounded" />
        <div className="h-8 w-24 bg-discord-light rounded-full" />
      </div>
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <div className="h-6 w-32 bg-discord-light rounded mb-4" />
        <div className="space-y-4">
          <div className="h-10 w-full bg-discord-light rounded" />
          <div className="h-10 w-full bg-discord-light rounded" />
          <div className="h-10 w-full bg-discord-light rounded" />
        </div>
      </div>
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
        <div className="h-6 w-40 bg-discord-light rounded mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-10 bg-discord-light rounded" />
          <div className="h-10 bg-discord-light rounded" />
          <div className="h-10 bg-discord-light rounded" />
          <div className="h-10 bg-discord-light rounded" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Bot configuration page component
 *
 * Provides a comprehensive form for configuring Discord bots with:
 * - Basic information (name, description)
 * - Discord token management
 * - LiteLLM endpoint selection for all AI services
 * - Voice configuration and wake phrase settings
 * - Auto-start and auto-restart toggles
 */
export function BotConfig() {
  const navigate = useNavigate();
  const { id: botId } = useParams<{ id: string }>();
  useAuth(); // Ensure user is authenticated
  const { effectiveTenantId } = useTenant();

  // State
  const [bot, setBot] = useState<Bot | null>(null);
  const [formData, setFormData] = useState<BotFormData>(DEFAULT_FORM_DATA);
  const [endpoints, setEndpoints] = useState<EndpointsByType>({
    LLM: [],
    STT: [],
    TTS: [],
    IMAGE: [],
  });
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEndpointsLoading, setIsEndpointsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showToken, setShowToken] = useState(false);

  /**
   * Fetch bot data
   */
  const fetchBot = useCallback(async () => {
    if (!botId) return;

    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.get<{ bot: Bot }>(`/bots/${botId}`);

      if (isSuccess(result)) {
        const botData = result.data.bot || result.data;
        setBot(botData as Bot);
        setFormData({
          name: botData.name,
          description: botData.description || '',
          discordToken: '', // Don't show existing token
          autoStart: botData.autoStart,
          autoRestart: botData.autoRestart,
          config: {
            ...DEFAULT_FORM_DATA.config,
            ...botData.config,
          },
        });
      } else {
        setError(result.error.message || 'Failed to fetch bot');
      }
    } catch (err) {
      setError('Failed to load bot. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [botId]);

  /**
   * Fetch LiteLLM endpoints for dropdowns
   */
  const fetchEndpoints = useCallback(async () => {
    setIsEndpointsLoading(true);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.get<EndpointsByType>('/litellm/endpoints/by-type');

      if (isSuccess(result)) {
        setEndpoints(result.data);
      }
    } catch {
      // Endpoints are optional, don't show error
    } finally {
      setIsEndpointsLoading(false);
    }
  }, []);

  /**
   * Fetch voice profiles
   */
  const fetchVoiceProfiles = useCallback(async () => {
    const apiClient = getApiClient();

    try {
      const queryParams = effectiveTenantId
        ? `?tenantId=${effectiveTenantId}`
        : '';
      const result = await apiClient.get<{ profiles: VoiceProfile[] }>(
        `/voices${queryParams}`
      );

      if (isSuccess(result)) {
        setVoiceProfiles(result.data.profiles || []);
      }
    } catch {
      // Voice profiles are optional
    }
  }, [effectiveTenantId]);

  /**
   * Save bot configuration
   */
  const handleSave = useCallback(async () => {
    if (!botId) return;

    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    const apiClient = getApiClient();

    // Build update payload
    const updateData: Partial<BotFormData> = {
      name: formData.name,
      description: formData.description || undefined,
      autoStart: formData.autoStart,
      autoRestart: formData.autoRestart,
      config: formData.config,
    };

    // Only include token if it was changed
    if (formData.discordToken) {
      updateData.discordToken = formData.discordToken;
    }

    try {
      const result = await apiClient.put<{ bot: Bot }>(`/bots/${botId}`, updateData);

      if (isSuccess(result)) {
        const botData = result.data.bot || result.data;
        setBot(botData as Bot);
        setSaveSuccess(true);
        // Clear token field after save
        setFormData((prev) => ({ ...prev, discordToken: '' }));
        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(result.error.message || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save configuration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [botId, formData]);

  /**
   * Handle form field changes
   */
  const handleFieldChange = useCallback(
    (field: keyof BotFormData, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  /**
   * Handle config field changes
   */
  const handleConfigChange = useCallback(
    (field: keyof BotConfig, value: string | number | boolean | undefined) => {
      setFormData((prev) => ({
        ...prev,
        config: { ...prev.config, [field]: value },
      }));
    },
    []
  );

  /**
   * Handle search config field changes
   */
  const handleSearchConfigChange = useCallback(
    (field: keyof SearchConfigData, value: string | number | boolean) => {
      setFormData((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          search: {
            ...prev.config.search,
            [field]: value,
          } as SearchConfigData,
        },
      }));
    },
    []
  );

  /**
   * Restore default search tool prompt
   */
  const restoreDefaultToolPrompt = useCallback(() => {
    handleSearchConfigChange('toolPrompt', DEFAULT_SEARCH_TOOL_PROMPT);
  }, [handleSearchConfigChange]);

  /**
   * Handle free text mode config changes
   */
  const handleFreeTextModeChange = useCallback(
    (field: keyof FreeTextModeConfig, value: boolean | string | number | undefined) => {
      setFormData((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          freeTextMode: {
            ...prev.config.freeTextMode,
            enabled: prev.config.freeTextMode?.enabled ?? false,
            [field]: value,
          } as FreeTextModeConfig,
        },
      }));
    },
    []
  );

  /**
   * Handle free voice mode config changes
   */
  const handleFreeVoiceModeChange = useCallback(
    (field: keyof FreeVoiceModeConfig, value: boolean | string | number | undefined) => {
      setFormData((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          freeVoiceMode: {
            ...prev.config.freeVoiceMode,
            enabled: prev.config.freeVoiceMode?.enabled ?? false,
            [field]: value,
          } as FreeVoiceModeConfig,
        },
      }));
    },
    []
  );

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchBot();
    void fetchEndpoints();
    void fetchVoiceProfiles();
  }, [fetchBot, fetchEndpoints, fetchVoiceProfiles]);

  if (isLoading) {
    return (
      <Layout>
        <LoadingSkeleton />
      </Layout>
    );
  }

  if (!bot) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-12">
          <svg
            className="h-12 w-12 text-gray-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-medium text-white">Bot not found</h3>
          <p className="mt-1 text-sm text-gray-400">
            The requested bot could not be found.
          </p>
          <button
            onClick={() => navigate('/bots')}
            className="mt-4 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
          >
            Back to Bots
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/bots')}
              className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors"
              title="Back to bots"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">{bot.name}</h1>
              <p className="text-sm text-gray-400">Configure bot settings</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={bot.status} />
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {saveSuccess && (
          <div className="rounded-md bg-green-900/50 border border-green-500 p-4">
            <div className="flex items-center">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <p className="text-sm text-green-200">Configuration saved successfully!</p>
            </div>
          </div>
        )}

        {/* Basic Settings */}
        <FormSection
          title="Basic Settings"
          description="Configure the bot's name, description, and Discord credentials"
        >
          <FormInput
            label="Bot Name"
            id="name"
            value={formData.name}
            onChange={(value) => handleFieldChange('name', value)}
            placeholder="My Discord Bot"
            required
          />
          <FormInput
            label="Description"
            id="description"
            type="textarea"
            value={formData.description}
            onChange={(value) => handleFieldChange('description', value)}
            placeholder="A helpful bot for my server..."
            rows={2}
          />
          <div>
            <FormInput
              label="Discord Token"
              id="discordToken"
              type={showToken ? 'text' : 'password'}
              value={formData.discordToken}
              onChange={(value) => handleFieldChange('discordToken', value)}
              placeholder="Enter new token to update..."
              helpText="Leave blank to keep the existing token"
            />
            <button
              type="button"
              onClick={() => setShowToken(!showToken)}
              className="mt-1 text-xs text-discord-blurple hover:text-discord-blurple/80"
            >
              {showToken ? 'Hide' : 'Show'} token
            </button>
          </div>
        </FormSection>

        {/* AI Endpoints */}
        <FormSection
          title="AI Service Endpoints"
          description="Select LiteLLM endpoints for different AI capabilities"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EndpointSelect
              label="LLM Endpoint"
              type="LLM"
              value={formData.config.llmEndpointId}
              onChange={(value) => handleConfigChange('llmEndpointId', value)}
              endpoints={endpoints.LLM}
              isLoading={isEndpointsLoading}
              helpText="Language model for chat responses"
            />
            <EndpointSelect
              label="TTS Endpoint"
              type="TTS"
              value={formData.config.ttsEndpointId}
              onChange={(value) => handleConfigChange('ttsEndpointId', value)}
              endpoints={endpoints.TTS}
              isLoading={isEndpointsLoading}
              helpText="Text-to-speech for voice responses"
            />
            <EndpointSelect
              label="STT Endpoint"
              type="STT"
              value={formData.config.sttEndpointId}
              onChange={(value) => handleConfigChange('sttEndpointId', value)}
              endpoints={endpoints.STT}
              isLoading={isEndpointsLoading}
              helpText="Speech-to-text for voice input"
            />
            <EndpointSelect
              label="Image Endpoint"
              type="IMAGE"
              value={formData.config.imageEndpointId}
              onChange={(value) => handleConfigChange('imageEndpointId', value)}
              endpoints={endpoints.IMAGE}
              isLoading={isEndpointsLoading}
              helpText="Image generation capabilities"
            />
          </div>
        </FormSection>

        {/* Voice Settings */}
        <FormSection
          title="Voice Settings"
          description="Configure voice interaction and TTS settings"
        >
          <VoiceProfileSelect
            value={formData.config.voiceProfileId}
            onChange={(value) => handleConfigChange('voiceProfileId', value)}
            profiles={voiceProfiles}
            isLoading={isEndpointsLoading}
          />
          <FormInput
            label="Wake Phrase"
            id="wakePhrase"
            value={formData.config.wakePhrase || ''}
            onChange={(value) => handleConfigChange('wakePhrase', value)}
            placeholder="hey bot"
            helpText="Phrase to trigger the bot in voice channels"
          />
          <FormInput
            label="Voice Idle Timeout"
            id="voiceIdleTimeout"
            type="number"
            value={formData.config.voiceIdleTimeout || 300}
            onChange={(value) =>
              handleConfigChange('voiceIdleTimeout', parseInt(value, 10) || 300)
            }
            min={30}
            max={3600}
            helpText="Seconds before bot leaves inactive voice channel (30-3600)"
          />
          <FormToggle
            label="Voice Trigger Enabled"
            description="Allow bot to respond to voice commands"
            checked={formData.config.voiceTriggerEnabled ?? true}
            onChange={(checked) => handleConfigChange('voiceTriggerEnabled', checked)}
          />
        </FormSection>

        {/* Chat Settings */}
        <FormSection
          title="Chat Settings"
          description="Configure chat behavior and memory"
        >
          <FormInput
            label="System Prompt"
            id="systemPrompt"
            type="textarea"
            value={formData.config.systemPrompt || ''}
            onChange={(value) => handleConfigChange('systemPrompt', value)}
            placeholder="You are a helpful assistant..."
            rows={4}
            helpText="Initial instructions for the AI model"
          />
          <FormInput
            label="Memory Size"
            id="memorySize"
            type="number"
            value={formData.config.memorySize || 20}
            onChange={(value) =>
              handleConfigChange('memorySize', parseInt(value, 10) || 20)
            }
            min={1}
            max={100}
            helpText="Number of messages to keep in conversation history (1-100)"
          />
        </FormSection>

        {/* Free Mode Settings */}
        <FormSection
          title="Free Mode Settings"
          description="Configure free chat and voice modes where the bot responds without needing to be mentioned"
        >
          <div className="space-y-6">
            {/* Free Text Mode */}
            <div className="border-b border-discord-light pb-4">
              <h4 className="text-md font-medium text-white mb-2">Free Text Mode</h4>
              <p className="text-xs text-gray-500 mb-4">
                When enabled, users can use /free on in a text channel to make the bot respond to all messages without needing a mention.
              </p>
              <FormToggle
                label="Allow Free Text Mode"
                description="Master toggle - enables the /free command for text channels"
                checked={formData.config.freeTextMode?.enabled ?? false}
                onChange={(checked) => handleFreeTextModeChange('enabled', checked)}
              />
              {formData.config.freeTextMode?.enabled && (
                <div className="mt-4 space-y-4">
                  <FormInput
                    label="Free Text Mode System Prompt"
                    id="freeTextSystemPrompt"
                    type="textarea"
                    value={formData.config.freeTextMode?.systemPrompt || ''}
                    onChange={(value) => handleFreeTextModeChange('systemPrompt', value || undefined)}
                    placeholder="Leave blank to use the default system prompt..."
                    rows={3}
                    helpText="Alternative system prompt when in free text mode (leave blank to use default)"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      label="Min Response Delay (seconds)"
                      id="freeTextDelayMin"
                      type="number"
                      value={formData.config.freeTextMode?.delayMin ?? 3}
                      onChange={(value) => handleFreeTextModeChange('delayMin', parseInt(value, 10) || 0)}
                      min={0}
                      max={60}
                      helpText="Minimum delay before responding"
                    />
                    <FormInput
                      label="Max Response Delay (seconds)"
                      id="freeTextDelayMax"
                      type="number"
                      value={formData.config.freeTextMode?.delayMax ?? 7}
                      onChange={(value) => handleFreeTextModeChange('delayMax', parseInt(value, 10) || 0)}
                      min={0}
                      max={60}
                      helpText="Maximum delay before responding"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Free Voice Mode */}
            <div>
              <h4 className="text-md font-medium text-white mb-2">Free Voice Mode</h4>
              <p className="text-xs text-gray-500 mb-4">
                When enabled, users can use /join-free to have the bot join voice and respond to all speech without the wake phrase.
              </p>
              <FormToggle
                label="Allow Free Voice Mode"
                description="Master toggle - enables the /join-free command for voice channels"
                checked={formData.config.freeVoiceMode?.enabled ?? false}
                onChange={(checked) => handleFreeVoiceModeChange('enabled', checked)}
              />
              {formData.config.freeVoiceMode?.enabled && (
                <div className="mt-4 space-y-4">
                  <FormInput
                    label="Free Voice Mode System Prompt"
                    id="freeVoiceSystemPrompt"
                    type="textarea"
                    value={formData.config.freeVoiceMode?.systemPrompt || ''}
                    onChange={(value) => handleFreeVoiceModeChange('systemPrompt', value || undefined)}
                    placeholder="Leave blank to use the default system prompt..."
                    rows={3}
                    helpText="Alternative system prompt when in free voice mode (leave blank to use default)"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      label="Min Response Delay (seconds)"
                      id="freeVoiceDelayMin"
                      type="number"
                      value={formData.config.freeVoiceMode?.delayMin ?? 3}
                      onChange={(value) => handleFreeVoiceModeChange('delayMin', parseInt(value, 10) || 0)}
                      min={0}
                      max={60}
                      helpText="Minimum delay before responding"
                    />
                    <FormInput
                      label="Max Response Delay (seconds)"
                      id="freeVoiceDelayMax"
                      type="number"
                      value={formData.config.freeVoiceMode?.delayMax ?? 7}
                      onChange={(value) => handleFreeVoiceModeChange('delayMax', parseInt(value, 10) || 0)}
                      min={0}
                      max={60}
                      helpText="Maximum delay before responding"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </FormSection>

        {/* Search Settings */}
        <FormSection
          title="Web Search Settings"
          description="Configure SearXNG web search integration and LLM tool calling"
        >
          <FormToggle
            label="Enable Web Search"
            description="Allow the bot to search the web for information"
            checked={formData.config.search?.enabled ?? true}
            onChange={(checked) => handleSearchConfigChange('enabled', checked)}
          />
          {formData.config.search?.enabled && (
            <>
              <FormInput
                label="Max Search Results"
                id="searchMaxResults"
                type="number"
                value={formData.config.search?.maxResults ?? 5}
                onChange={(value) =>
                  handleSearchConfigChange('maxResults', parseInt(value, 10) || 5)
                }
                min={1}
                max={10}
                helpText="Maximum number of search results to include (1-10)"
              />
              <FormToggle
                label="Include in LLM Context"
                description="Automatically include search results in conversation context"
                checked={formData.config.search?.includeInContext ?? true}
                onChange={(checked) => handleSearchConfigChange('includeInContext', checked)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Safe Search Level
                </label>
                <select
                  value={formData.config.search?.safeSearchLevel ?? 1}
                  onChange={(e) =>
                    handleSearchConfigChange('safeSearchLevel', parseInt(e.target.value, 10))
                  }
                  className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                >
                  <option value={0}>Off</option>
                  <option value={1}>Moderate</option>
                  <option value={2}>Strict</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Filter explicit content from search results</p>
              </div>
              <div className="border-t border-discord-light pt-4 mt-4">
                <FormToggle
                  label="Enable LLM Tool Calling"
                  description="Allow the LLM to autonomously trigger web searches (requires tool-capable model)"
                  checked={formData.config.search?.toolCallEnabled ?? false}
                  onChange={(checked) => handleSearchConfigChange('toolCallEnabled', checked)}
                />
              </div>
              {formData.config.search?.toolCallEnabled && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-300">
                      Search Tool Prompt
                      <span className="text-gray-500 text-xs ml-2">(injected into system prompt)</span>
                    </label>
                    <button
                      type="button"
                      onClick={restoreDefaultToolPrompt}
                      className="text-xs text-discord-blurple hover:text-discord-blurple/80"
                    >
                      Restore Defaults
                    </button>
                  </div>
                  <textarea
                    value={formData.config.search?.toolPrompt ?? DEFAULT_SEARCH_TOOL_PROMPT}
                    onChange={(e) => handleSearchConfigChange('toolPrompt', e.target.value)}
                    rows={6}
                    className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:border-discord-blurple focus:ring-discord-blurple font-mono text-sm"
                    placeholder="Instructions for the LLM on when to use search..."
                  />
                  <p className="text-xs text-gray-500">
                    Customize how the LLM decides when to search. This is added to the system prompt when tool calling is enabled.
                  </p>
                </div>
              )}
            </>
          )}
        </FormSection>

        {/* Bot Behavior */}
        <FormSection
          title="Bot Behavior"
          description="Configure automatic startup and recovery settings"
        >
          <FormToggle
            label="Auto Start"
            description="Automatically start bot when the system starts"
            checked={formData.autoStart}
            onChange={(checked) => handleFieldChange('autoStart', checked)}
          />
          <FormToggle
            label="Auto Restart"
            description="Automatically restart bot if it crashes"
            checked={formData.autoRestart}
            onChange={(checked) => handleFieldChange('autoRestart', checked)}
          />
        </FormSection>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate('/bots')}
            className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !formData.name}
            className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </Layout>
  );
}

export default BotConfig;
